/**
 * Import Service API - Dashboard, Jobs, Configurations, Middleware Status
 */
import api, { type PaginatedResponse } from './client';

// ============================================================================
// Types
// ============================================================================

export interface ImportJob {
  id: number;
  job_id: string;
  correlation_id: string;
  data_type: string;
  data_type_display: string;
  source: string;
  source_display: string;
  is_incremental: boolean;
  customer: number;
  customer_name: string;
  status: string;
  status_display: string;
  total_records: number;
  records_created: number;
  records_updated: number;
  records_skipped: number;
  records_failed: number;
  records_processed: number;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  error_message: string;
  created_at: string;
  updated_at: string;
}

export interface HealthCheckDescriptor {
  id: string;
  name: string;
  description: string;
}

export type HealthCheckStatus = 'pass' | 'warning' | 'fail' | 'error';

export interface HealthCheckResponse {
  check_id: string;
  customer_id: number;
  status: HealthCheckStatus;
  summary: string;
  metrics: Record<string, unknown>;
  samples: Array<Record<string, unknown>>;
  duration_ms: number;
}

export interface ImportJobDetail extends ImportJob {
  error_traceback: string;
  source_file_name: string;
  celery_task_id: string;
  error_breakdown: Array<{ error_code: string; count: number }>;
  debug_sample_raw: Array<Record<string, unknown>>;
  debug_sample_processed: Array<Record<string, unknown>>;
}

export interface ImportLog {
  id: number;
  action: string;
  action_display: string;
  entity_type: string;
  entity_id: number | null;
  entity_reference: string;
  source_row: number | null;
  source_key: string;
  fields_changed: Record<string, unknown>;
  error_code: string;
  error_message: string;
  created_at: string;
}

export interface ImportConfiguration {
  id: number;
  customer: number;
  customer_name: string;
  data_type: string;
  data_type_display: string;
  schedule_type: string;
  schedule_type_display: string;
  cron_expression: string;
  interval_minutes: number | null;
  is_incremental: boolean;
  batch_size: number;
  max_retries: number;
  retry_delay_seconds: number;
  source_config: Record<string, unknown>;
  field_mappings: Record<string, unknown>;
  auto_create_dependencies: boolean;
  validate_before_import: boolean;
  is_active: boolean;
  last_run_at: string | null;
  last_success_at: string | null;
  consecutive_failures: number;
  created_at: string;
  updated_at: string;
}

export interface DashboardSummary {
  total_jobs: number;
  jobs_today: number;
  jobs_running: number;
  jobs_failed_today: number;
  records_created_today: number;
  records_updated_today: number;
  records_skipped_today: number;
  records_failed_today: number;
  by_data_type: Array<{
    data_type: string;
    total: number;
    succeeded: number;
    failed: number;
    records_created: number;
    records_failed: number;
  }>;
  recent_failures: ImportJob[];
}

export interface MiddlewareQueue {
  queue_key: string;
  depth: number;
  customer: string;
  customer_id: number;
  data_type: string;
  configuration_id: number;
}

/** Per-cron heartbeat reported by the .NET middleware (PNA only). */
export interface MiddlewareHealthSource {
  source: string;
  last_seen_at: string | null;
  stale_seconds: number | null;
  last_status: string;
  last_cron: string;
  last_records: number | null;
  last_duration_ms: number | null;
  last_error_message: string;
}

/** Append-only event reported by the .NET middleware on caught failures. */
export interface MiddlewareHealthEvent {
  occurred_at: string | null;
  severity: 'info' | 'warning' | 'error' | 'fatal';
  source: string;
  code: string;
  message: string;
  sap_status_code: number | null;
  sap_page: number | null;
  correlation_id: string;
}

export interface MiddlewareHealth {
  sources: MiddlewareHealthSource[];
  recent_events: MiddlewareHealthEvent[];
  overall: 'green' | 'yellow' | 'red';
  min_last_seen_at: string | null;
  /** ISO timestamp of the freshest heartbeat across all sources. */
  most_recent_seen_at?: string | null;
  max_stale_seconds: number | null;
  /** Seconds since the freshest heartbeat across all sources — drives the banner. */
  min_stale_seconds?: number | null;
}

export interface MiddlewareStatusResponse {
  queues: MiddlewareQueue[];
  error?: string;
  /** Present only for PNA (source=pna). Undefined for other tenants. */
  health?: MiddlewareHealth;
}

/** Import source for switching between P&A, Atlas Copco, Poeton, and Data Import views */
export type ImportSource = 'pna' | 'atlascopco' | 'poeton' | 'data_import';

export interface ImportSourceOption {
  key: string;
  source: ImportSource;
  customer_id: number;
  customer_name: string;
}

export interface ImportJobFilters {
  customer?: number;
  data_type?: string;
  source?: string;
  status?: string;
  created_after?: string;
  created_before?: string;
  has_failures?: boolean;
  search?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
}

// ============================================================================
// API
// ============================================================================

function jobsPrefix(source: ImportSource): string {
  switch (source) {
    case 'atlascopco': return '/imports/atlascopco-jobs/';
    case 'poeton': return '/imports/poeton-jobs/';
    case 'data_import': return '/imports/data-import-jobs/';
    default: return '/imports/jobs/';
  }
}

export const importsAPI = {
  getSources: () =>
    api.get<ImportSourceOption[]>('/imports/sources/').then(r => r.data),

  getDashboard: (source: ImportSource = 'pna') =>
    api.get<DashboardSummary>('/imports/dashboard/', { params: { source } }).then(r => r.data),

  getJobs: (filters?: ImportJobFilters, source: ImportSource = 'pna') => {
    const prefix = jobsPrefix(source);
    return api.get<PaginatedResponse<ImportJob>>(prefix, { params: filters }).then(r => r.data);
  },

  getJob: (jobId: string, source: ImportSource = 'pna') => {
    const prefix = jobsPrefix(source);
    return api.get<ImportJobDetail>(`${prefix}${jobId}/`).then(r => r.data);
  },

  getJobLogs: (
    jobId: string,
    params?: { action?: string; entity_type?: string; error_code?: string; page?: number },
    source: ImportSource = 'pna',
  ) => {
    const prefix = jobsPrefix(source);
    return api.get<PaginatedResponse<ImportLog>>(`${prefix}${jobId}/logs/`, { params }).then(r => r.data);
  },

  triggerImport: (data: { data_type: string; customer_id: number; configuration_id?: number }) =>
    api.post<ImportJob>('/imports/jobs/', data).then(r => r.data),

  getConfigurations: (filters?: { customer?: number; data_type?: string; is_active?: boolean }) =>
    api.get<PaginatedResponse<ImportConfiguration>>('/imports/configurations/', { params: filters }).then(r => r.data),

  getMiddlewareStatus: (source: ImportSource = 'pna') =>
    api.get<MiddlewareStatusResponse>('/imports/middleware-status/', { params: { source } }).then(r => r.data),

  listHealthChecks: () =>
    api.get<{ checks: HealthCheckDescriptor[] }>('/imports/health-checks/').then(r => r.data.checks),

  runHealthCheck: (checkId: string, customerId: number) =>
    api.post<HealthCheckResponse>(
      `/imports/health-checks/${checkId}/run/`,
      { customer_id: customerId },
    ).then(r => r.data),

  cancelJob: (jobId: string) =>
    api.post<{ message: string; status: string }>(`/imports/jobs/${jobId}/cancel/`).then(r => r.data),
};
